import { useMemo, useState } from "react";
import { feedItems, feedTabs, type FeedItem } from "@/mocks/gossip";

const typeBadge: Record<string, string> = {
  reply: "bg-accent-100 text-accent-800",
  joke: "bg-primary-100 text-primary-700",
  gossip: "bg-secondary-100 text-secondary-800",
  meme: "bg-accent-100 text-accent-800",
};

const typeIcon: Record<string, string> = {
  reply: "ri-lightbulb-flash-line",
  joke: "ri-emotion-laugh-line",
  gossip: "ri-chat-3-line",
  meme: "ri-emotion-laugh-fill",
};

function FeedCard({ item, index }: { item: FeedItem; index: number }) {
  const [liked, setLiked] = useState(false);
  const shown = item.like + (liked ? 1 : 0);

  return (
    <article className="animate-pop-in flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5 transition-all hover:-translate-y-1 hover:border-primary-200 hover:shadow-soft md:p-6">
      <div className="flex items-center justify-between">
        <span
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${typeBadge[item.type]}`}
        >
          <i className={typeIcon[item.type]}></i>
          {item.tag}
        </span>
        <span className="whitespace-nowrap text-[11px] text-foreground-400">#{String(index + 1).padStart(2, "0")}</span>
      </div>

      <p className="mt-4 text-xs font-semibold text-primary-600 md:text-sm">{item.topic}</p>
      <p className="mt-3 flex-1 text-sm leading-7 text-foreground-800 md:text-[15px]">
        {item.content}
      </p>

      <div className="mt-5 flex items-center justify-between border-t border-background-100 pt-4">
        <div className="flex items-center gap-2">
          <img
            src={item.avatar}
            alt={item.author}
            className="h-9 w-9 rounded-full border-2 border-background-100 object-cover"
            loading="lazy"
          />
          <span className="whitespace-nowrap text-xs font-medium text-foreground-600">{item.author}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <button
            onClick={() => setLiked(!liked)}
            aria-label="点赞"
            className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 transition-colors ${
              liked ? "text-primary-600" : "hover:text-primary-600"
            }`}
          >
            <i className={`${liked ? "ri-heart-fill" : "ri-heart-line"} text-sm`}></i>
            <span>{shown.toLocaleString("zh-CN")}</span>
          </button>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-chat-smile-2-line text-sm"></i>
            {item.comment}
          </span>
          <button aria-label="分享" className="cursor-pointer rounded-full px-1 hover:text-primary-600">
            <i className="ri-share-forward-line text-sm"></i>
          </button>
        </div>
      </div>
    </article>
  );
}

export default function FeedSection() {
  const [active, setActive] = useState("all");
  const list = useMemo(() => {
    const source = feedItems.filter((f) => f.type !== "joke");
    return active === "all" ? source : source.filter((f) => f.type === active);
  }, [active]);

  return (
    <section id="comments" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-16 md:px-6 md:py-24">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <div>
          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
            <i className="ri-chat-smile-3-fill"></i>
            高能评论区
          </span>
          <h2 className="mt-3 font-display text-3xl font-black text-foreground-900 md:text-4xl">
            今日神回复，一条比一条精彩
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-500 md:text-base">
            评论区才是真正的正片。精选当日最高能的热评与神回复，保证每条都能让你拍案叫绝。
          </p>
        </div>
        <a
          href="#newsletter"
          className="hidden whitespace-nowrap rounded-full border border-primary-200 px-5 py-2.5 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50 md:inline-flex"
        >
          我也有梗，去投稿
        </a>
      </div>

      <div className="mt-8 inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-background-100 p-1 no-scrollbar">
        {feedTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
              active === t.key
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-primary-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-2">
        {list.map((item, i) => (
          <FeedCard key={item.id} item={item} index={i} />
        ))}
      </div>
    </section>
  );
}